/* Wave 2 PDP enhancements: mobile sticky add-to-cart bar and cross-product
   option pricing. Plain asset by design (no webpack).
   Survives AJAX product swaps by re-syncing on PUB_SUB_EVENTS.variantChange —
   HTMLUpdateUtility.viewTransition replaces the product-info element, so
   nothing here may hold references into it across swaps.

   Loaded by both sections/pf-product-scroll.liquid and sections/main-product
   .liquid. The sticky bar stays behind the scroll gate in init(); cross-product
   option pricing works off the option fieldsets alone and runs on any product
   template. Keeps the pf-scroll name because the scroll section is still its
   main consumer and renaming would churn both sections plus the asset URL. */
(() => {
  "use strict";
  if (window.pfScrollEnhancements) return;
  window.pfScrollEnhancements = true;

  const getScrollProductInfo = () =>
    document.querySelector("product-info[data-pf-scroll-product-info]");

  const getSelectedVariant = () => {
    const script = getScrollProductInfo()?.querySelector(
      'variant-selects:not([data-upsell-isolated="true"]) [data-selected-variant]'
    );
    try {
      return script ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  };

  const moneyFmt = new Intl.NumberFormat(document.documentElement.lang || "en", {
    style: "currency",
    currency: window.Shopify?.currency?.active || "USD",
  });
  const formatMoney = (cents) => moneyFmt.format(cents / 100).replace(/\.00$/, "");

  // sessionStorage-cached product JSON (10 min TTL); memoized in-flight so
  // concurrent callers share one fetch
  const PJSON_TTL = 10 * 60 * 1000;
  const pjsonMemo = new Map();
  const fetchProductJson = (url) => {
    const path = url.split("?")[0];
    if (pjsonMemo.has(path)) return pjsonMemo.get(path);
    const key = `pf:pjson:${path}`;
    try {
      const hit = JSON.parse(sessionStorage.getItem(key));
      if (hit && Date.now() - hit.t < PJSON_TTL) {
        const promise = Promise.resolve(hit.p);
        pjsonMemo.set(path, promise);
        return promise;
      }
    } catch {
      /* fall through to network */
    }
    const promise = fetch(`${path}.js`)
      .then((response) =>
        response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))
      )
      .then((product) => {
        try {
          sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), p: product }));
        } catch {
          /* storage full — memo cache still applies */
        }
        return product;
      });
    pjsonMemo.set(path, promise);
    promise.catch(() => pjsonMemo.delete(path));
    return promise;
  };

  // Variant-state bus: every feature re-syncs from the published variant object
  let lastVariant = null;
  const listeners = [];
  const onVariantState = (fn) => listeners.push(fn);
  const emit = () => {
    const variant = lastVariant || getSelectedVariant();
    listeners.forEach((fn) => {
      try {
        fn(variant);
      } catch {
        /* one feature failing must not break the others */
      }
    });
  };

  /* --- Mobile sticky add-to-cart bar --- */
  function initStickyAtc() {
    const productInfo = getScrollProductInfo();
    const sectionId = productInfo.dataset.section;

    const bar = document.createElement("div");
    bar.className = "pf-scroll-sticky-atc";
    bar.hidden = true;
    bar.innerHTML = `
      <div class="pf-scroll-sticky-atc__meta">
        <span class="pf-scroll-sticky-atc__price" data-sticky-price></span>
        <span class="pf-scroll-sticky-atc__condition" data-sticky-condition></span>
      </div>
      <button type="submit" name="add" form="product-form-${sectionId}"
        class="button button--primary pf-scroll-sticky-atc__submit" data-sticky-submit>
        <span data-sticky-label></span>
      </button>`;
    document.body.appendChild(bar);

    const price = bar.querySelector("[data-sticky-price]");
    const condition = bar.querySelector("[data-sticky-condition]");
    const submit = bar.querySelector("[data-sticky-submit]");
    const label = bar.querySelector("[data-sticky-label]");
    let stickyVariant = null;

    const setPurchaseMode = () => {
      submit.type = "submit";
      submit.name = "add";
      submit.setAttribute("form", `product-form-${sectionId}`);
      submit.disabled = false;
      delete submit.dataset.backInStockTrigger;
      label.textContent = window.variantStrings?.addToCart || "Add to cart";
    };

    const setSoldOutMode = () => {
      const backInStockReady = window.plugBackInStock?.ready === true;
      submit.type = "button";
      submit.removeAttribute("name");
      submit.removeAttribute("form");
      submit.disabled = !backInStockReady;
      if (backInStockReady) {
        submit.dataset.backInStockTrigger = "";
        label.textContent =
          window.plugBackInStock?.labels?.cta || window.variantStrings?.soldOut || "";
      } else {
        delete submit.dataset.backInStockTrigger;
        label.textContent = window.variantStrings?.soldOut || "";
      }
    };

    const sync = (variant) => {
      stickyVariant = variant;
      if (!variant) {
        submit.type = "button";
        submit.removeAttribute("name");
        submit.removeAttribute("form");
        submit.disabled = true;
        delete submit.dataset.backInStockTrigger;
        label.textContent = window.variantStrings?.unavailable || "Unavailable";
        price.textContent = "";
        condition.textContent = "";
        return;
      }
      price.textContent = formatMoney(variant.price);
      condition.textContent =
        (variant.options || []).filter((o) => o !== "Default Title").slice(-1)[0] || "";
      const soldOut = variant.available === false;
      if (soldOut) {
        setSoldOutMode();
      } else {
        setPurchaseMode();
      }
    };

    // Shared back-in-stock component contract. Keeping the handoff event-only
    // prevents this PF Scroll furniture from owning or duplicating signup UI.
    submit.addEventListener("click", (event) => {
      if (
        !stickyVariant ||
        stickyVariant.available !== false ||
        window.plugBackInStock?.ready !== true
      )
        return;
      event.preventDefault();
      document.dispatchEvent(
        new CustomEvent("plug:back-in-stock-open", {
          detail: {
            surface: "mobile_sticky",
            variantId: String(stickyVariant.id),
          },
        })
      );
    });
    document.addEventListener("plug:back-in-stock-ready", () => {
      sync(lastVariant || getSelectedVariant());
    });

    // Bar shows only once the shopper has scrolled past the reviews section,
    // and never while the real buy box is on screen (mobile-only via CSS) —
    // earlier in the page it just eats viewport without helping.
    let buyBoxVisible = false;
    let pastReviews = false;
    const applyVisibility = () => {
      bar.hidden = buyBoxVisible || !pastReviews;
    };

    let observer = null;
    const observeBuyBox = () => {
      const target = document.querySelector(
        "product-info[data-pf-scroll-product-info] .pf-scroll-product-info__buy-buttons"
      );
      observer?.disconnect();
      if (!target) {
        buyBoxVisible = false;
        applyVisibility();
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => {
          buyBoxVisible = entry.isIntersecting;
          applyVisibility();
        },
        { rootMargin: "0px 0px -10% 0px" }
      );
      observer.observe(target);
    };

    let reviewsObserver = null;
    const observeReviews = (attempt = 0) => {
      const reviews = document.querySelector(".junip-product-review");
      if (!reviews) {
        if (attempt < 15) {
          // Junip can hydrate late — retry before falling back
          setTimeout(() => observeReviews(attempt + 1), 1000);
        } else {
          pastReviews = true; // no reviews section on this page: old behavior
          applyVisibility();
        }
        return;
      }
      reviewsObserver?.disconnect();
      reviewsObserver = new IntersectionObserver(([entry]) => {
        pastReviews = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        applyVisibility();
      });
      reviewsObserver.observe(reviews);
    };

    onVariantState((variant) => {
      sync(variant);
      observeBuyBox();
      // viewTransition removes the swapped-out node up to 500ms later — observe
      // again once the fresh node is the only one left
      setTimeout(observeBuyBox, 600);
    });
    sync(getSelectedVariant());
    observeBuyBox();
    observeReviews();
  }

  /* --- Cross-product option rows (Storage, Connectivity) ---
     Combined listings render these values as links to sibling products, so
     price and availability never reach this page's variant data and the rows
     render bare. Fetch each sibling and decorate it with the same
     .pf-pill-tag / .pf-pill-price markup product-variant-options.liquid gives
     the Condition rows, so every ladder in the buy box reads alike:

       128GB                                       $316.99
       256GB   SOLD OUT                            +$53.00
       512GB   SOLD OUT                           +$114.00

     Every rung is priced on the Condition the shopper currently has selected,
     so the deltas are pure storage steps.

     Condition fieldsets are skipped — Liquid already prices those, and it can
     use inventory_quantity for "Only N left", which /products/x.js omits. */
  function initCrossProductHints() {
    // Liquid prices the Condition pills with the `money` filter, which always
    // keeps cents — formatMoney's trailing-.00 strip would render "+$50" next
    // to Condition's "+$10.00". Match Liquid here, leave the sticky bar alone.
    const rowMoney = (cents) => moneyFmt.format(cents / 100);

    // Values locked in on axes that live inside a single product (Condition).
    // Cross-product axes are skipped — those are what varies between siblings.
    const heldOptions = () => {
      const held = new Map();
      document.querySelectorAll("fieldset[data-option]").forEach((fieldset) => {
        const crossProduct = [...fieldset.querySelectorAll("input[data-product-url]")].some(
          (input) => (input.dataset.productUrl || "").trim()
        );
        if (crossProduct) return;
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) held.set(fieldset.dataset.option, checked.value);
      });
      return held;
    };

    // The sibling's variant on the same Condition the shopper is looking at.
    // Comparing cheapest-in-stock instead compares whatever happens to be in
    // stock on each side — on a phone whose 128GB sells out of Good but whose
    // 256GB sells out of Good and Great, that prices 256GB/Excellent against
    // 128GB/Great and reports two condition steps as a storage upgrade fee.
    const matchVariant = (product, held) => {
      const positionOf = new Map(
        (product.options || []).map((option, index) => [
          String(option?.name ?? option)
            .toLowerCase()
            .trim(),
          typeof option?.position === "number" ? option.position - 1 : index,
        ])
      );
      const matches = product.variants.filter((variant) =>
        [...held].every(([name, value]) => {
          const index = positionOf.get(name);
          return index == null || variant.options[index] === value;
        })
      );
      // No counterpart for this Condition in the sibling: fall back to its floor
      const pool = matches.length ? matches : product.variants;
      return pool.reduce(
        (low, variant) => (low && low.price <= variant.price ? low : variant),
        null
      );
    };

    const decorateRow = (row, variant, anchor) => {
      const content = row.label.querySelector(".label-content");
      if (!content) return;
      const soldOut = variant.available === false;
      const price = variant.price;

      let tag = content.querySelector(".pf-pill-tag--soldout");
      if (soldOut && !tag) {
        tag = document.createElement("span");
        tag.className = "pf-pill-tag pf-pill-tag--soldout";
        tag.textContent = window.variantStrings?.soldOut || "Sold out";
        content.appendChild(tag);
      } else if (!soldOut && tag) {
        tag.remove();
      }

      let priceEl = row.label.querySelector(".pf-pill-price");
      if (price == null) {
        priceEl?.remove();
      } else {
        if (!priceEl) {
          priceEl = document.createElement("span");
          priceEl.className = "pf-pill-price";
          // Sibling of .label-content so the label's space-between pushes it right
          content.after(priceEl);
        }
        priceEl.classList.toggle("pf-pill-price--soldout", soldOut);
        // Sold-out rows keep their delta. The upgrade costs what it costs
        // whether or not it's in stock today, and an absolute price on one rung
        // of a delta ladder reads as if that rung were the cheap one.
        const text =
          anchor != null && price > anchor ? `+${rowMoney(price - anchor)}` : rowMoney(price);
        // Only write a changed price, and edit the text node in place. Setting
        // textContent swaps the node (a childList change), which both this file's
        // clone observer and pf-scroll-pilot-enhancements.js answer by re-pricing
        // the pills, so the two took turns on every frame. An in-place edit is a
        // characterData change neither observer watches. The page still shows the
        // same prices as before: this script writes last.
        if (priceEl.textContent !== text) {
          const textNode = priceEl.childNodes.length === 1 ? priceEl.firstChild : null;
          if (textNode?.nodeType === Node.TEXT_NODE) textNode.data = text;
          else priceEl.textContent = text;
        }
      }

      row.label.classList.toggle("pf-pill-row--soldout", soldOut);
    };

    const decorateFieldset = async (fieldset) => {
      const rows = [...fieldset.querySelectorAll('input[type="radio"][data-product-url]')]
        .map((input) => ({
          label: input.nextElementSibling,
          url: (input.dataset.productUrl || "").split("?")[0],
        }))
        .filter((row) => row.url && row.label?.tagName === "LABEL");
      if (rows.length < 2) return;

      const products = await Promise.all(
        rows.map((row) => fetchProductJson(row.url).catch(() => null))
      );
      const held = heldOptions();
      const variants = products.map((product) => (product ? matchVariant(product, held) : null));
      // Anchor on the cheapest rung, in stock or not — same rule as
      // product-variant-options.liquid uses for the Condition pills, so a row
      // selling out never shifts the baseline under the rest of the ladder.
      const anchor = variants.reduce(
        (low, variant) => (variant && (low == null || variant.price < low) ? variant.price : low),
        null
      );

      rows.forEach((row, index) => {
        if (variants[index]) decorateRow(row, variants[index], anchor);
      });
    };

    // Hidden source fieldset AND rendered clones: clones inherit source
    // decorations on re-clone, this pass covers already-rendered ones
    const decorate = () => {
      document
        .querySelectorAll('fieldset.product-form__input--pill:not([data-option="condition"])')
        .forEach(decorateFieldset);
    };

    onVariantState(() => setTimeout(decorate, 650));
    // The scroll-row option targets live outside product-info, so these
    // observers survive swaps; re-clone = childList mutation
    let queued = false;
    const cloneObserver = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        decorate();
      });
    });
    document
      .querySelectorAll(".pf-product-scroll__variant-option")
      .forEach((target) => cloneObserver.observe(target, { childList: true, subtree: true }));
    decorate();
  }

  const init = () => {
    const isScrollPdp = Boolean(getScrollProductInfo());
    // Combined-listing rows carry a sibling product URL; a plain in-product
    // option carries an empty one. No such rows and no scroll section means
    // there is nothing on this page for any of these features to do.
    const hasCrossProductRows = document.querySelector(
      'fieldset.product-form__input--pill input[type="radio"][data-product-url]:not([data-product-url=""])'
    );
    if (!isScrollPdp && !hasCrossProductRows) return;

    // Pricing first: it is the customer-facing half, and the sticky bar
    // constructs IntersectionObservers that would otherwise take the whole
    // init down with them on a browser that lacks one.
    initCrossProductHints();
    if (isScrollPdp) {
      initStickyAtc();
    }
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        lastVariant = event?.data?.variant ?? null;
        // double rAF: let HTMLUpdateUtility insert the new product-info first
        requestAnimationFrame(() => requestAnimationFrame(emit));
      });
    }
    emit();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
